import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  ArrowUp,
  ChevronDown,
  ExternalLink,
  File,
  FileImage,
  FileText,
  MessageCircleMore,
  Minimize2,
  Paperclip,
  Search,
  Smile,
  Users,
} from 'lucide-react';
import {
  fetchChatBootstrap,
  fetchConversationMessages,
  markConversationAsRead,
  openChatAttachment,
  openDirectConversation,
  resolveChatAssetUrl,
  sendConversationMessage,
} from '@/services/chat.js';
import api from '@/api/axiosConfig.js';
import Swal from 'sweetalert2';

export async function clearConversationMessages(conversationId) {
  const response = await api.delete(`/api/chat/conversations/${conversationId}/messages`);
  return response.data?.data ?? null;
}

export async function deleteConversation(conversationId) {
  const response = await api.delete(`/api/chat/conversations/${conversationId}`);
  return response.data?.data ?? null;
}

const OPERATOR_MODE_KEY = 'sinamon.chat.operator-mode';

const EMOJIS = ['😀', '😂', '😉', '😍', '🤝', '🔥', '👏', '🚚', '📦', '✅', '⚡', '💬'];

function formatTime(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

function formatLastSeen(value) {
  if (!value) return 'Sin actividad reciente';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(0, Math.round(diffMs / 60000));
  if (diffMin <= 1) return 'Activo ahora';
  if (diffMin < 60) return `Activo hace ${diffMin} min`;
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function initialsFromName(user) {
  const name = [user?.nombre, user?.apellido].filter(Boolean).join(' ').trim() || user?.nombreCompleto || user?.email || 'U';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('');
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
      dataUrl: String(reader.result || ''),
    });
    reader.onerror = () => reject(new Error(`No pudimos leer ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function StatusBadge({ user }) {
  const isOnline = Boolean(user?.isOnline);
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
        isOnline
          ? 'border-[rgba(var(--brand),0.18)] bg-[rgba(var(--brand),0.12)] text-[rgba(var(--fg),0.87)]'
          : 'border-[rgba(var(--border),0.18)] bg-[rgba(var(--subpanel),0.88)] text-[rgba(var(--fg),0.6)]'
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${isOnline ? 'bg-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.7)]'}`} />
      {isOnline ? 'Activo' : 'Inactivo'}
    </span>
  );
}

function getUserPhotoUrl(user) {
  return user?.photoUrl || user?.avatarUrl || user?.fotoUrl || user?.foto || user?.imagen || '';
}

function getAvatarTone(name = '') {
  const tones = [
    'bg-[rgba(var(--brand),0.16)] text-[rgba(var(--fg),0.92)]',
    'bg-[rgba(var(--subpanel),0.92)] text-[rgba(var(--fg),0.82)]',
    'bg-[rgba(var(--panel),0.86)] text-[rgba(var(--fg),0.82)]',
    'bg-[rgba(var(--brand),0.1)] text-[rgba(var(--fg),0.88)]',
  ];
  const hash = Array.from(String(name)).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return tones[hash % tones.length];
}

function isImageAttachment(attachment) {
  const mimeType = String(attachment?.tipo || attachment?.type || '').toLowerCase();
  const fileName = String(attachment?.nombre || attachment?.name || '').toLowerCase();
  if (mimeType.startsWith('image/')) {
    return true;
  }
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName);
}

function getAttachmentKind(attachment) {
  const mimeType = String(attachment?.tipo || attachment?.type || '').toLowerCase();
  const fileName = String(attachment?.nombre || attachment?.name || '').toLowerCase();

  if (isImageAttachment(attachment)) return 'image';
  if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) return 'pdf';
  if (mimeType.includes('word') || /\.(doc|docx)$/i.test(fileName)) return 'doc';
  return 'file';
}

function ChatAvatar({ user, size = 'md', active = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  const photoUrl = resolveChatAssetUrl(getUserPhotoUrl(user));
  const hasPhoto = Boolean(photoUrl) && !imageFailed;
  const name = user?.nombreCompleto || user?.email || 'Usuario';
  const sizeClass = size === 'sm' ? 'h-9 w-9 text-[11px]' : 'h-11 w-11 text-sm';

  return (
    <div className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-2xl font-semibold ${sizeClass}`}>
      {hasPhoto ? (
        <img
          src={photoUrl}
          alt={name}
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className={`flex h-full w-full items-center justify-center ${active ? 'bg-[rgba(var(--brand),0.16)] text-[rgba(var(--fg),0.92)]' : getAvatarTone(name)}`}>
          {initialsFromName(user)}
        </div>
      )}
      <span
        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[rgba(var(--panel),0.92)] ${
          user?.isOnline ? 'bg-[rgb(var(--brand))]' : 'bg-[rgba(var(--muted),0.72)]'
        }`}
        aria-hidden="true"
      />
    </div>
  );
}

function AttachmentPreview({ attachment, onError, compact = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  const kind = getAttachmentKind(attachment);
  const previewUrl = resolveChatAssetUrl(attachment?.url || attachment?.downloadUrl);
  const isImage = kind === 'image' && !imageFailed && Boolean(previewUrl);

  const handleOpen = () => {
    openChatAttachment(attachment).catch((error) => {
      onError?.(error?.response?.data?.message || error?.message || 'No pudimos abrir el adjunto.');
    });
  };

  if (isImage) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Abrir imagen adjunta ${attachment.nombre}`}
        className="group flex w-full max-w-[260px] flex-col overflow-hidden rounded-2xl border border-[rgba(var(--border),0.14)] bg-[rgba(var(--panel),0.86)] text-left transition duration-def ease-bezier hover:bg-[rgba(var(--panel),0.94)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
      >
        <div className={`relative overflow-hidden bg-[rgba(var(--subpanel),0.88)] ${compact ? 'max-h-[140px]' : 'max-h-[180px]'}`}>
          <img
            src={previewUrl}
            alt={attachment.nombre}
            className="h-full w-full object-cover transition duration-def ease-bezier group-hover:scale-[1.01]"
            onError={() => setImageFailed(true)}
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2">
          <FileImage size={14} className="shrink-0 text-[rgb(var(--brand))]" />
          <span className="truncate text-xs font-medium text-[rgba(var(--fg),0.82)]">{attachment.nombre}</span>
        </div>
      </button>
    );
  }

  const icon = kind === 'pdf'
    ? <FileText size={14} className="shrink-0 text-[rgb(var(--brand))]" />
    : kind === 'doc'
      ? <FileText size={14} className="shrink-0 text-[rgba(var(--fg),0.72)]" />
      : <File size={14} className="shrink-0 text-[rgba(var(--fg),0.72)]" />;

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label={`Abrir adjunto ${attachment.nombre}`}
      className="inline-flex items-center gap-2 rounded-xl border border-[rgba(var(--border),0.14)] bg-[rgba(var(--panel),0.88)] px-3 py-2 text-xs font-medium text-[rgba(var(--fg),0.87)] shadow-[0_10px_22px_rgba(var(--shadow),0.06)] transition duration-def ease-bezier hover:-translate-y-0.5 hover:bg-[rgba(var(--panel),0.96)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
    >
      {icon}
      <span className="truncate">{attachment.nombre}</span>
    </button>
  );
}

function AttachmentLink({ attachment, onError, compact = false }) {
  return (
    <AttachmentPreview attachment={attachment} onError={onError} compact={compact} />
  );
}

export default function ChatPage() {
  const location = useLocation();
  const messagesViewportRef = useRef(null);
  const searchInputRef = useRef(null);
  const draftInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeConversationRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const [bootstrap, setBootstrap] = useState({ me: null, contacts: [], conversations: [], onlineWindowSeconds: 90, chatUnavailable: false });
  const [loadingBootstrap, setLoadingBootstrap] = useState(true);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [references, setReferences] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [chatUnavailable, setChatUnavailable] = useState(false);
  const [unreadMarkerCount, setUnreadMarkerCount] = useState(0);
  const [isPopout, setIsPopout] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, conversationId: null });
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [operatorMode, setOperatorMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.localStorage.getItem(OPERATOR_MODE_KEY) === '1';
  });

  const activeConversation = useMemo(
    () => bootstrap.conversations.find(item => item.id === activeConversationId) || null,
    [bootstrap.conversations, activeConversationId],
  );

  const filteredContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return []; // Ocultar si no hay búsqueda
    return bootstrap.contacts.filter((item) => {
      const haystack = [item.nombre, item.apellido, item.nombreCompleto, item.email].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [bootstrap.contacts, search]);

  const lastOwnMessage = useMemo(
    () => [...messages].reverse().find(item => String(item.senderUserId) === String(bootstrap.me?.id) && item.body)?.body || '',
    [messages, bootstrap.me?.id],
  );

  const unreadSeparatorIndex = useMemo(() => {
    if (!messages.length || unreadMarkerCount <= 0 || unreadMarkerCount >= messages.length) {
      return -1;
    }
    return messages.length - unreadMarkerCount;
  }, [messages.length, unreadMarkerCount]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu({ visible: false, x: 0, y: 0, conversationId: null });
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleContextMenu = (event, conversationId) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      conversationId,
    });
  };

  const handleClearChat = async (conversationId) => {
    try {
      if (!window.confirm('¿Estás seguro de que quieres vaciar este chat? No se podrán recuperar los mensajes.')) {
        return;
      }
      await clearConversationMessages(conversationId);
      if (activeConversationId === conversationId) {
        setMessages([]);
      }
      setBootstrap(prev => ({
        ...prev,
        conversations: prev.conversations.map(item => (
          item.id === String(conversationId)
            ? { ...item, lastMessage: null, lastMessageAt: null, unreadCount: 0 }
            : item
        )),
      }));
      setContextMenu({ visible: false, x: 0, y: 0, conversationId: null });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'No pudimos vaciar el chat.');
    }
  };

  const handleDeleteChat = async (conversationId) => {
    try {
      const result = await Swal.fire({
        title: '¿Eliminar chat?',
        text: 'No se podrá recuperar.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
      });

      if (!result.isConfirmed) {
        return;
      }

      await deleteConversation(conversationId);
      setBootstrap(prev => {
        const conversations = prev.conversations.filter(item => item.id !== String(conversationId));
        if (activeConversationId === String(conversationId)) {
          setActiveConversationId(conversations[0]?.id ?? null);
          setMessages([]);
        }
        return {
          ...prev,
          conversations,
        };
      });
      setContextMenu({ visible: false, x: 0, y: 0, conversationId: null });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'No pudimos eliminar el chat.');
    }
  };

  useEffect(() => {
    activeConversationRef.current = activeConversationId;
  }, [activeConversationId]);

  const scrollToBottom = (behavior = 'auto') => {
    const node = messagesViewportRef.current;
    if (node) {
      node.scrollTo({ top: node.scrollHeight, behavior });
    }
  };

  // Scroll al fondo al cambiar de conversación (inmediato, sin animación)
  useEffect(() => {
    const nextUnreadCount = Number(activeConversation?.unreadCount || 0);
    setUnreadMarkerCount(nextUnreadCount > 0 ? nextUnreadCount : 0);
    shouldStickToBottomRef.current = true;
    setShowJumpToBottom(false);
    // Doble RAF: espera a que el DOM se pinte antes de scrollear
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
  }, [activeConversation?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(OPERATOR_MODE_KEY, operatorMode ? '1' : '0');
  }, [operatorMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select?.();
        return;
      }

      if (event.key === 'Escape') {
        setDraft('');
        setShowEmojiPicker(false);
        return;
      }

      if (
        event.key === 'ArrowUp'
        && document.activeElement === draftInputRef.current
        && !draft.trim()
        && !attachments.length
        && !references.length
        && lastOwnMessage
      ) {
        event.preventDefault();
        setDraft(lastOwnMessage);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [attachments.length, draft, lastOwnMessage, references.length]);

  useEffect(() => {
    let mounted = true;

    const loadBootstrap = async ({ silent = false } = {}) => {
      if (!silent) {
        setLoadingBootstrap(true);
      }

      try {
        const next = await fetchChatBootstrap();
        if (!mounted) return;

        setBootstrap(next);
        setChatUnavailable(Boolean(next?.chatUnavailable));
        setErrorMessage(next?.chatUnavailable ? 'El backend de chat no esta disponible en este entorno.' : '');

        if (!activeConversationRef.current && next.conversations.length > 0) {
          setActiveConversationId(next.conversations[0].id);
        }
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(error?.response?.data?.message || 'No pudimos cargar el modulo de chat.');
      } finally {
        if (mounted && !silent) {
          setLoadingBootstrap(false);
        }
      }
    };

    loadBootstrap();
    const intervalId = window.setInterval(() => {
      loadBootstrap({ silent: true });
    }, 6000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [bootstrap.conversations, activeConversationId]);

  useEffect(() => {
    if (chatUnavailable || !activeConversationId) {
      setMessages([]);
      return undefined;
    }

    let mounted = true;

    const loadMessages = async ({ silent = false } = {}) => {
      if (!silent) {
        setLoadingMessages(true);
      }

      try {
        const nextMessages = await fetchConversationMessages(activeConversationId, 80);
        if (!mounted) return;
        setMessages(Array.isArray(nextMessages) ? nextMessages : nextMessages?.items || []);
        await markConversationAsRead(activeConversationId);
        setBootstrap(prev => ({
          ...prev,
          conversations: prev.conversations.map(item => (
            item.id === String(activeConversationId)
              ? { ...item, unreadCount: 0 }
              : item
          )),
        }));
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(error?.response?.data?.message || 'No pudimos cargar los mensajes.');
      } finally {
        if (mounted && !silent) {
          setLoadingMessages(false);
        }
      }
    };

    loadMessages();
    const intervalId = window.setInterval(() => {
      loadMessages({ silent: true });
    }, 4000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [activeConversationId, chatUnavailable]);

  // Stick al fondo cuando llegan mensajes nuevos
  useLayoutEffect(() => {
    if (shouldStickToBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [messages.length]);

  useEffect(() => {
    if (!activeConversationId) {
      return;
    }
    draftInputRef.current?.focus();
  }, [activeConversationId]);

  const handleSelectContact = async (contact) => {
    if (chatUnavailable) {
      return;
    }

    if (contact.conversationId) {
      setActiveConversationId(contact.conversationId);
      return;
    }

    try {
      const conversation = await openDirectConversation(contact.id);
      if (conversation?.id) {
        setActiveConversationId(conversation.id);
        const next = await fetchChatBootstrap();
        setBootstrap(next);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'No pudimos abrir esa conversacion.');
    }
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []).slice(0, 5);
    event.target.value = '';

    if (!files.length) {
      return;
    }

    try {
      const nextFiles = await Promise.all(files.map(fileToDataUrl));
      setAttachments(prev => [...prev, ...nextFiles].slice(0, 5));
    } catch (error) {
      setErrorMessage(error.message || 'No pudimos preparar los adjuntos.');
    }
  };

  const handleShareCurrentView = () => {
    const pathLabel = location.pathname === '/chat' ? 'Modulo de chat interno' : `Vista ${location.pathname}`;
    setReferences(prev => [
      ...prev,
      {
        title: pathLabel,
        url: location.pathname,
        kind: 'internal',
      },
    ]);
  };

  const handleSend = async () => {
    if (chatUnavailable || !activeConversationId || sending) {
      return;
    }

    if (!draft.trim() && !attachments.length && !references.length) {
      return;
    }

    setSending(true);
    shouldStickToBottomRef.current = true;

    try {
      await sendConversationMessage(activeConversationId, {
        text: draft,
        attachments,
        references,
      });

      setDraft('');
      setAttachments([]);
      setReferences([]);
      setShowEmojiPicker(false);
      setUnreadMarkerCount(0);

      const [nextMessages, nextBootstrap] = await Promise.all([
        fetchConversationMessages(activeConversationId, 80),
        fetchChatBootstrap(),
      ]);

      setMessages(Array.isArray(nextMessages) ? nextMessages : nextMessages?.items || []);
      setBootstrap(nextBootstrap);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'No pudimos enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  const totalUnread = bootstrap.conversations.reduce((acc, item) => acc + Number(item.unreadCount || 0), 0);
  const onlineCount = bootstrap.contacts.filter(item => item.isOnline).length;

  const chatContent = (
    <section className={`grid min-h-0 flex-1 gap-1 overflow-hidden lg:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]`}>
      <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[rgba(var(--border),0.15)] bg-[rgba(var(--subpanel),0.6)]">
        <div className="border-b border-[rgba(var(--border),0.1)] px-2 py-1.5">
          <div className="flex items-center gap-2 rounded-lg border border-[rgba(var(--border),0.12)] bg-[rgba(var(--panel),0.8)] px-2 py-1 focus-within:ring-1 focus-within:ring-[rgb(var(--ring))]">
            <Search size={13} className="text-[rgba(var(--fg),0.3)]" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar..."
              className="w-full bg-transparent text-[11.5px] text-[rgba(var(--fg),0.8)] outline-none placeholder:text-[rgba(var(--fg),0.35)]"
            />
          </div>
        </div>

        <div className="border-b border-[rgba(var(--border),0.1)] px-2 py-1">
          <div className="mb-0.5 flex items-center gap-2 px-1 text-[8px] font-bold uppercase tracking-wider text-[rgba(var(--fg),0.4)]">
            <Users size={11} />
            Buscar {search ? '' : '(Escribí para buscar)'}
          </div>
          {filteredContacts.length > 0 && (
            <div className="flex max-h-32 min-h-0 flex-col gap-0.5 overflow-y-auto pr-1">
              {filteredContacts.map(contact => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleSelectContact(contact)}
                  className="w-full overflow-hidden rounded-lg px-2 py-1 text-left transition hover:bg-[rgba(var(--panel),0.5)]"
                >
                  <div className="flex items-center gap-2">
                    <ChatAvatar user={contact} size="sm" />
                    <p className="truncate text-[11.5px] font-medium text-[rgba(var(--fg),0.75)]">{contact.nombreCompleto}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
          <div className="mb-0.5 flex items-center justify-between px-1">
            <span className="text-[8px] font-bold uppercase tracking-wider text-[rgba(var(--fg),0.4)]">
              Chats
            </span>
          </div>

          <div className="space-y-0.5">
            {bootstrap.conversations.map(conversation => {
              const selected = conversation.id === activeConversationId;
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setActiveConversationId(conversation.id)}
                  onContextMenu={(e) => handleContextMenu(e, conversation.id)}
                  className={`group relative w-full overflow-hidden rounded-lg px-2 py-1 text-left transition ${
                    selected
                      ? 'bg-[rgba(var(--panel),0.9)] shadow-sm ring-1 ring-[rgba(var(--ring),0.1)]'
                      : 'hover:bg-[rgba(var(--panel),0.4)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ChatAvatar user={conversation.participant} size="sm" active={selected} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`truncate text-[11.5px] ${conversation.unreadCount > 0 ? 'font-bold text-[rgba(var(--fg),0.9)]' : 'font-medium text-[rgba(var(--fg),0.75)]'}`}>
                          {conversation.participant.nombreCompleto}
                        </p>
                        <span className="shrink-0 text-[8px] text-[rgba(var(--fg),0.35)]">
                          {formatTime(conversation.lastMessage?.createdAt || conversation.updatedAt)}
                        </span>
                      </div>
                      <p className={`truncate text-[10px] ${conversation.unreadCount > 0 ? 'font-medium text-[rgba(var(--fg),0.6)]' : 'text-[rgba(var(--fg),0.45)]'}`}>
                        {conversation.lastMessage?.preview || 'Empezar...'}
                      </p>
                    </div>
                    {conversation.unreadCount > 0 && (
                      <span className="flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-[rgb(var(--brand))] px-1 text-[8.5px] font-bold text-white">
                        {conversation.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[rgba(var(--border),0.15)] bg-[rgba(var(--panel),0.7)]">
        {activeConversation ? (
          <>
            <header className="shrink-0 border-b border-[rgba(var(--border),0.08)] bg-[rgba(var(--panel),0.4)] px-3 py-1.5 backdrop-blur">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <ChatAvatar user={activeConversation.participant} size="sm" active />
                  <div className="min-w-0">
                    <h2 className="truncate text-[13px] font-bold text-[rgba(var(--fg),0.85)]">
                      {activeConversation.participant.nombreCompleto}
                    </h2>
                    <p className="truncate text-[10px] font-medium text-[rgba(var(--fg),0.4)]">
                      {activeConversation.participant.isOnline ? 'En línea' : `Visto ${formatLastSeen(activeConversation.participant.lastSeenAt)}`}
                    </p>
                  </div>
                </div>
              </div>
            </header>

            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[rgba(var(--bg),0.2)]">
              <div
                ref={messagesViewportRef}
                onScroll={(event) => {
                  const node = event.currentTarget;
                  const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
                  shouldStickToBottomRef.current = distanceFromBottom < 80;
                  setShowJumpToBottom(distanceFromBottom >= 80);
                }}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-2 pt-2"
              >
              {loadingMessages ? (
                <div className="py-8 text-center text-[11px] font-medium text-[rgba(var(--fg),0.3)]">Cargando...</div>
              ) : (
                <div className="space-y-1">
                  {messages.map((message, index) => {
                    const mine = String(message.senderUserId) === String(bootstrap.me?.id);
                    return (
                      <div key={message.id}>
                        {index === unreadSeparatorIndex && (
                          <div className="my-1.5 flex items-center gap-2">
                            <div className="h-px flex-1 bg-[rgba(var(--border),0.05)]" />
                            <span className="text-[8.5px] font-bold uppercase tracking-wider text-[rgb(var(--brand))] opacity-70">Nuevos mensajes</span>
                            <div className="h-px flex-1 bg-[rgba(var(--border),0.05)]" />
                          </div>
                        )}

                        <article className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                          <div className={`flex max-w-[85%] flex-col gap-0.5 ${mine ? 'items-end' : 'items-start'}`}>
                            <div
                              className={`rounded-xl px-2.5 py-1 ${
                                mine
                                  ? 'rounded-br-none bg-[rgb(var(--brand))] text-white shadow-sm'
                                  : 'rounded-bl-none border border-[rgba(var(--border),0.1)] bg-[rgba(var(--subpanel),0.95)] text-[rgba(var(--fg),0.85)] shadow-xs'
                              }`}
                            >
                              {message.body && (
                                <p className="whitespace-pre-wrap text-[12px] leading-tight-normal">{message.body}</p>
                              )}

                              {message.references?.length > 0 && (
                                <div className="mt-1 flex flex-col gap-1">
                                  {message.references.map(reference => (
                                    <RouterLink
                                      key={reference.url}
                                      to={reference.url}
                                      className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[10.5px] font-bold ${
                                        mine ? 'bg-white/10 text-white' : 'bg-[rgba(var(--panel),0.8)] text-[rgb(var(--brand))]'
                                      }`}
                                    >
                                      <FileText size={11} />
                                      {reference.title}
                                    </RouterLink>
                                  ))}
                                </div>
                              )}

                              {message.attachments?.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {message.attachments.map(att => (
                                    <AttachmentLink key={att.id} attachment={att} compact onError={setErrorMessage} />
                                  ))}
                                </div>
                              )}
                              
                              <div className={`flex items-center justify-end gap-1 text-[8.5px] font-medium opacity-60 ${mine ? 'text-white' : 'text-[rgba(var(--fg),0.4)]'}`}>
                                {formatTime(message.createdAt)}
                              </div>
                            </div>
                          </div>
                        </article>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>

              {showJumpToBottom && (
                <button
                  type="button"
                  onClick={() => {
                    shouldStickToBottomRef.current = true;
                    setShowJumpToBottom(false);
                    scrollToBottom('smooth');
                  }}
                  className="absolute bottom-2 right-2 z-10 inline-flex h-7 items-center gap-1.5 rounded-full border border-[rgba(var(--brand),0.15)] bg-[rgba(var(--panel),0.95)] px-2.5 text-[10px] font-bold text-[rgba(var(--fg),0.8)] shadow-lg backdrop-blur"
                >
                  <ChevronDown size={12} />
                  Bajar
                </button>
              )}
            </div>

            <footer className="shrink-0 border-t border-[rgba(var(--border),0.1)] bg-[rgba(var(--panel),0.3)] px-3 py-1.5">
              {(attachments.length > 0 || references.length > 0) && (
                <div className="mb-1 flex flex-wrap gap-1">
                  {attachments.map(item => (
                    <button key={item.id} onClick={() => setAttachments(p => p.filter(f => f.id !== item.id))} className="flex items-center gap-1 rounded-md border border-[rgba(var(--border),0.08)] bg-[rgba(var(--subpanel),0.8)] px-1.5 py-0.5 text-[9.5px] font-bold text-[rgba(var(--fg),0.6)]">
                      <Paperclip size={10} /> {item.name}
                    </button>
                  ))}
                  {references.map(ref => (
                    <button key={ref.url} onClick={() => setReferences(p => p.filter(r => r !== ref))} className="flex items-center gap-1 rounded-md border border-[rgba(var(--brand),0.15)] bg-[rgba(var(--brand),0.04)] px-1.5 py-0.5 text-[9.5px] font-bold text-[rgb(var(--brand))]">
                      <FileText size={10} /> {ref.title}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-1.5 rounded-xl border border-[rgba(var(--border),0.12)] bg-[rgba(var(--panel),0.9)] p-1 focus-within:ring-1 focus-within:ring-[rgb(var(--ring))]">
                <div className="flex items-center">
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="flex h-7 w-7 items-center justify-center rounded-lg text-[rgba(var(--fg),0.3)] hover:bg-[rgba(var(--subpanel),0.6)] hover:text-[rgba(var(--fg),0.6)] transition-colors">
                    <Paperclip size={14} />
                  </button>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilesSelected} />
                </div>

                <textarea
                  ref={draftInputRef}
                  value={draft}
                  onChange={e => {
                    setDraft(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 100)}px`;
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                      e.target.style.height = 'auto';
                    }
                  }}
                  rows={1}
                  placeholder="Escribí..."
                  className="flex-1 resize-none bg-transparent px-1 py-1.5 text-[12.5px] leading-tight text-[rgba(var(--fg),0.9)] outline-none placeholder:text-[rgba(var(--fg),0.25)]"
                />

                <button
                  type="button"
                  onClick={handleSend}
                  disabled={sending || (!draft.trim() && !attachments.length && !references.length)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgb(var(--brand))] text-white shadow-md transition-all hover:brightness-110 active:scale-95 disabled:opacity-30 disabled:hover:scale-100"
                >
                  <ArrowUp size={15} />
                </button>
              </div>
            </footer>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center opacity-40">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.08)] text-[rgb(var(--brand))]">
              <MessageCircleMore size={20} />
            </div>
            <div className="space-y-0.5">
              <h2 className="text-[13px] font-bold text-[rgba(var(--fg),0.7)]">Sin chat activo</h2>
              <p className="text-[10px] text-[rgba(var(--fg),0.4)]">Seleccioná un contacto para comenzar.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-1 overflow-hidden bg-transparent pb-2">
      <section className="shrink-0 rounded-xl border border-[rgba(var(--border),0.2)] bg-[linear-gradient(180deg,rgba(var(--panel),0.98),rgba(var(--panel),0.94))] px-3 py-1 shadow-sm backdrop-blur">
        <div className="flex flex-col gap-1 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.2em] text-[rgba(var(--fg),0.5)]">
              <MessageCircleMore size={12} className="text-[rgb(var(--brand))]" />
              Chat interno
            </div>
            <div className="mt-0.5 flex flex-col gap-0.5 lg:flex-row lg:items-center lg:gap-3">
              <h1 className="text-[1.1rem] font-bold tracking-tight text-[rgba(var(--fg),0.87)]">Sinamon Chat</h1>
              <p className="text-[11px] text-[rgba(var(--fg),0.54)]">
                {operatorMode ? (bootstrap.me?.nombreCompleto || bootstrap.me?.email || 'Operador') : 'Conversaciones directas y contexto operativo.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 self-start xl:min-w-[400px] xl:justify-end">
            <button
              type="button"
              onClick={() => setIsPopout(prev => !prev)}
              title={isPopout ? "Volver a vista normal" : "Abrir en ventana flotante"}
              className="group flex h-7 w-7 items-center justify-center rounded-lg border border-[rgba(var(--border),0.22)] bg-[rgba(var(--subpanel),0.7)] text-[rgba(var(--fg),0.6)] transition hover:bg-[rgba(var(--subpanel),0.9)] hover:text-[rgba(var(--fg),0.9)]"
            >
              {isPopout ? <Minimize2 size={14} /> : <ExternalLink size={14} />}
            </button>

            <button
              type="button"
              onClick={() => setOperatorMode(prev => !prev)}
              className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition duration-def ease-bezier focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                operatorMode
                  ? 'border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.14)] text-[rgba(var(--fg),0.9)]'
                  : 'border-[rgba(var(--border),0.22)] bg-[rgba(var(--subpanel),0.7)] text-[rgba(var(--fg),0.72)] hover:bg-[rgba(var(--subpanel),0.86)]'
              }`}
            >
              {operatorMode ? 'Modo Operador' : 'Activar Modo Op.'}
            </button>

            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1.5 rounded-lg border border-[rgba(var(--border),0.18)] bg-[rgba(var(--subpanel),0.6)] px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[rgb(var(--brand))]" />
                <span className="text-[11px] font-bold text-[rgba(var(--fg),0.8)]">{onlineCount}</span>
                <span className="text-[10px] font-medium text-[rgba(var(--fg),0.5)] uppercase tracking-wider">Activos</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-lg border border-[rgba(var(--border),0.18)] bg-[rgba(var(--subpanel),0.6)] px-2 py-1">
                <span className="text-[11px] font-bold text-[rgba(var(--fg),0.8)]">{bootstrap.conversations.length}</span>
                <span className="text-[10px] font-medium text-[rgba(var(--fg),0.5)] uppercase tracking-wider">Chats</span>
              </div>
              <div className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 ${totalUnread > 0 ? 'border-[rgba(var(--brand),0.3)] bg-[rgba(var(--brand),0.1)]' : 'border-[rgba(var(--border),0.18)] bg-[rgba(var(--subpanel),0.6)]'}`}>
                <span className={`text-[11px] font-bold ${totalUnread > 0 ? 'text-[rgb(var(--brand))]' : 'text-[rgba(var(--fg),0.8)]'}`}>{totalUnread}</span>
                <span className="text-[10px] font-medium text-[rgba(var(--fg),0.5)] uppercase tracking-wider">Pendientes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {errorMessage && (
        <div
          className="mx-1 shrink-0 rounded-lg border border-[rgba(var(--brand),0.2)] bg-[rgba(var(--brand),0.05)] px-4 py-1.5 text-xs text-[rgba(var(--fg),0.8)]"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {!isPopout && chatContent}

      {isPopout && (
        <div 
          className="fixed bottom-4 right-4 z-[999] flex h-[600px] w-[900px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[rgba(var(--border),0.25)] bg-[rgba(var(--panel),0.98)] shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-xl"
        >
          <header className="flex h-10 shrink-0 items-center justify-between border-b border-[rgba(var(--border),0.1)] bg-[rgba(var(--subpanel),0.5)] px-4">
            <div className="flex items-center gap-2">
              <MessageCircleMore size={14} className="text-[rgb(var(--brand))]" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-[rgba(var(--fg),0.7)]">Sinamon Chat</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsPopout(false)}
                className="flex h-6 w-6 items-center justify-center rounded-lg hover:bg-[rgba(var(--fg),0.05)] text-[rgba(var(--fg),0.4)] hover:text-[rgba(var(--fg),0.8)] transition-colors"
                title="Minimizar a vista normal"
              >
                <Minimize2 size={14} />
              </button>
            </div>
          </header>
          <div className="flex-1 overflow-hidden p-1">
            {chatContent}
          </div>
        </div>
      )}

      {contextMenu.visible && (
        <div 
          className="fixed z-[9999] min-w-[140px] overflow-hidden rounded-lg border border-[rgba(var(--border),0.15)] bg-[rgba(var(--panel),0.98)] p-1 shadow-xl backdrop-blur-md"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => handleClearChat(contextMenu.conversationId)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[11.5px] font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.08)]"
          >
            <span>Vaciar chat</span>
          </button>
          <button
            type="button"
            onClick={() => handleDeleteChat(contextMenu.conversationId)}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[11.5px] font-medium text-[rgba(var(--fg),0.82)] transition hover:bg-[rgba(var(--fg),0.06)]"
          >
            <span>Eliminar chat</span>
          </button>
        </div>
      )}
    </div>
  );
}
